import { useQuery } from '@tanstack/react-query';
import { Autocomplete } from 'src/components/Autocomplete';
import { ListItem } from 'src/components/ListItem';
import { TextField } from 'src/components/TextField';
import { WorkoutPlan } from 'src/models/workout-plan/_types';
import { fetchWorkoutPlanOptions } from 'src/models/workout-plan/workout-plan';

interface WorkoutPlanFilterConfig {
  label: string;
  value: string | undefined;
  setValue: (newVal: string, close?: boolean) => void;
  valueOnly?: boolean;
  multiple?: boolean;
}

export const WorkoutPlanFilter: React.FC<WorkoutPlanFilterConfig> = ({
  label,
  value,
  setValue,
  valueOnly,
  multiple = true,
}) => {
  const { data: options = [], isLoading } = useQuery({
    queryKey: ['workoutPlans'],
    queryFn: () => fetchWorkoutPlanOptions(),
    staleTime: 60000,
  });

  const planIds = value ? String(value).split(',') : [];

  let selectedPlan = undefined as WorkoutPlan | undefined;
  let selectedPlans = [] as WorkoutPlan[];

  if (multiple) {
    selectedPlans = options.filter((p) => planIds?.includes(String(p.id)));
  } else {
    selectedPlan = options.find((p) => p.id === value);
  }

  if (valueOnly) {
    if (multiple) {
      return <>{selectedPlans?.map((p) => p.name)?.join(', ')}</>;
    }
    return <>{selectedPlan?.name || ''}</>;
  }

  return (
    <Autocomplete
      multiple={multiple}
      options={options}
      getOptionLabel={(plan: WorkoutPlan) => plan?.name || ''}
      value={multiple ? selectedPlans : selectedPlan || null}
      onChange={(_, newValue) => {
        if (multiple) {
          setValue(
            (Array.isArray(newValue) ? newValue : []).map((p) => p.id).join(',')
          );
        } else {
          setValue(String(newValue?.id || ''));
        }
      }}
      loading={isLoading}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder="All" />
      )}
      renderOption={(props, plan) => (
        <ListItem
          {...(props as any)}
          clickable
          primary={plan.name}
          key={`${plan.id}-${plan.name}`}
        />
      )}
    />
  );
};
